import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * ShareSheet — bottom sheet on mobile, dropdown popover on desktop.
 *
 * The component is intentionally framework-light (no headless-UI / vaul
 * dependency) so it can drop into existing pages without expanding the bundle.
 * It listens for outside clicks + Escape and traps focus inside the sheet.
 *
 * Props:
 *   open / onClose — controlled state
 *   anchorRef       — desktop popover positioning anchor (the Share button)
 *   title           — short heading shown above the options
 *   options         — [{ id, label, icon, onClick, description?, danger? }]
 */
const ShareSheet = ({ open, onClose, anchorRef, title, options }) => {
  const { t } = useLanguage();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );
  const sheetRef = useRef(null);

  useEffect(() => {
    const handler = (e) => setIsDesktop(e.matches);
    const mq = window.matchMedia('(min-width: 768px)');
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  // Close on Escape + outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onClick = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  // ------------------ Desktop popover ------------------
  if (isDesktop) {
    return (
      <div
        ref={sheetRef}
        role="dialog"
        aria-label={title}
        data-testid="share-sheet"
        className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
      >
        <div className="px-4 py-2.5 border-b border-slate-100">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{title}</p>
        </div>
        <div className="py-1.5">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => { opt.onClick(); onClose(); }}
              data-testid={`share-option-${opt.id}`}
              className={`w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${opt.danger ? 'text-red-600' : 'text-slate-700'}`}
            >
              <span aria-hidden className="mt-0.5 flex-shrink-0 w-5 text-center">{opt.icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium">{opt.label}</span>
                {opt.description && (
                  <span className="block text-xs text-slate-500 mt-0.5 leading-snug">{opt.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ------------------ Mobile bottom sheet ------------------
  return (
    <div className="fixed inset-0 z-50 flex items-end" role="dialog" aria-label={title} data-testid="share-sheet">
      <div className="absolute inset-0 bg-black/40 animate-in fade-in duration-200" onClick={onClose} />
      <div
        ref={sheetRef}
        className="relative w-full bg-white rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] animate-in slide-in-from-bottom duration-200"
      >
        <div className="flex justify-center pt-3 pb-1">
          <span className="h-1.5 w-10 rounded-full bg-slate-300" />
        </div>
        <div className="px-5 pb-1">
          <p className="text-base font-semibold text-slate-900">{title}</p>
        </div>
        <div className="px-2 pb-3">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => { opt.onClick(); onClose(); }}
              data-testid={`share-option-${opt.id}`}
              className={`w-full flex items-start gap-3 px-4 py-3.5 text-left rounded-xl active:bg-slate-100 transition-colors ${opt.danger ? 'text-red-600' : 'text-slate-800'}`}
            >
              <span aria-hidden className="mt-0.5 flex-shrink-0 w-6 text-center text-lg">{opt.icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium">{opt.label}</span>
                {opt.description && (
                  <span className="block text-xs text-slate-500 mt-0.5 leading-snug">{opt.description}</span>
                )}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={onClose}
            className="w-full mt-1 px-4 py-3 text-sm font-medium text-slate-600 rounded-xl hover:bg-slate-100"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareSheet;
